import React from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import Seo from '../../components/Seo';
import { device } from '../../utils/breakpoints';
import PageContentWrapper from '../../components/pageElements/PageContentWrapper';
import PageContainer from '../../components/PageContainer';
import { getSortedPosts } from '../../data/blog/posts';

const BreadcrumbBar = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid #e5e7eb;
  font-size: 15px;
  color: #000000;

  a {
    color: #2563eb;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  span.sep {
    margin: 0 8px;
    color: #000000;
  }
`;

const Hero = styled.header`
  padding: 52px 0 40px;
  border-bottom: 1px solid #e5e7eb;
`;

const HeroEyebrow = styled.p`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #2563eb;
  margin: 0 0 12px;
`;

const HeroTitle = styled.h1`
  margin: 0 0 16px;
  font-size: 44px;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: #052475;

  @media ${device.tablet} {
    font-size: 32px;
  }
`;

const HeroIntro = styled.p`
  margin: 0;
  max-width: 860px;
  font-size: 17px;
  line-height: 1.55;
  color: #000000;
`;

const Section = styled.section`
  padding: 42px 0 74px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media ${device.laptop} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 220px;
  text-decoration: none;
  color: inherit;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  background: #fff;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: #bfdbfe;
    box-shadow: 0 10px 28px rgba(37, 99, 235, 0.1);
    text-decoration: none;
  }
`;

const CardPartner = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;
  margin-bottom: 14px;

  img {
    max-height: 36px;
    max-width: 110px;
    width: auto;
    height: auto;
    object-fit: contain;
  }
`;

const CardSuseMark = styled.span`
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #30ba78;
  line-height: 1;
`;

const CardTag = styled.p`
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #2563eb;
`;

const CardMeta = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  color: #64748b;
`;

const CardTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 22px;
  line-height: 1.25;
  color: #000000;
`;

const CardDesc = styled.p`
  margin: 0;
  flex: 1;
  font-size: 15px;
  line-height: 1.55;
  color: #334155;
`;

const CardFooter = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #2563eb;

  ${Card}:hover & {
    gap: 10px;
  }

  &::after {
    content: '→';
  }
`;

const BlogIndexPage = () => {
  const posts = getSortedPosts();

  return (
    <PageContentWrapper $noBottomPad>
      <Seo
        title="Blog"
        description="News, partnerships, and platform updates from the Ametnes team."
      />

      <BreadcrumbBar>
        <PageContainer>
          <Link to="/">Home</Link>
          <span className="sep">/</span>
          <span style={{ color: '#000000' }}>Blog</span>
        </PageContainer>
      </BreadcrumbBar>

      <Hero>
        <PageContainer>
          <HeroEyebrow>Blog</HeroEyebrow>
          <HeroTitle>News and updates</HeroTitle>
          <HeroIntro>
            Partnership announcements, platform news, and perspectives on enterprise open source from
            Ametnes.
          </HeroIntro>
        </PageContainer>
      </Hero>

      <Section>
        <PageContainer>
          <CardGrid>
            {posts.map((post) => (
              <Card to={`/blog/${post.slug}`} key={post.slug}>
                {(post.logoSrc || post.partnerName) && (
                  <CardPartner>
                    {post.logoSrc ? (
                      <img src={post.logoSrc} alt="" loading="lazy" decoding="async" />
                    ) : (
                      <CardSuseMark aria-hidden>{post.partnerName}</CardSuseMark>
                    )}
                  </CardPartner>
                )}
                <CardTag>{post.category}</CardTag>
                <CardMeta>{post.displayDate}</CardMeta>
                <CardTitle>{post.title}</CardTitle>
                <CardDesc>{post.excerpt}</CardDesc>
                <CardFooter>{post.cardFooterLabel || 'Read announcement'}</CardFooter>
              </Card>
            ))}
          </CardGrid>
        </PageContainer>
      </Section>
    </PageContentWrapper>
  );
};

export default BlogIndexPage;
